import { useTranslation } from "react-i18next";
import { Button as AriaButton } from "react-aria-components";
import { ChevronRight, Home } from "lucide-react";
import type { FacilityTreeNode } from "../facility-tree.js";
import { NavButton } from "@/components/ui/button";
import { CategoryIcon, FacilityIcon, ManualIcon } from "@/components/icons";
import { facilityDrillHref, facilityManualsHref } from "./nav-state.js";

/** How many direct children the panel lists before "Show all N" (which drills into the facility). */
const CHILD_PREVIEW = 6;

/**
 * The drill-down's preview panel (redesign 2026-10-21): the SELECTED card's facility, shown beside the list on wide
 * screens only — the host mounts it behind `useMediaQuery`, so on narrow screens it is absent, not merely hidden.
 *
 * - Banner on the tenant's paired `primary` / `primary-foreground` (navy + white for XR, red + white for Takeda; the
 *   lightened dark-mode partner + dark ink in XR · Dark) with the parent path, the name and three count tiles.
 * - **Manuals** (brand orange) and **Unterbereiche** — same gates as the card; each is omitted when it doesn't apply.
 * - The first {@link CHILD_PREVIEW} direct children as rows: a click navigates INTO this facility with that child
 *   selected (`onPickChild`); "Show all N" drills into the facility.
 *
 * Read-only by design: no star or kebab here — the card on the left carries both, and the existing star/kebab controls
 * are tuned for light surfaces, not this banner.
 */
export function FacilityPreviewPanel({
  node,
  parentLabel,
  onOpenManuals,
  onDrill,
  onPickChild,
}: {
  node: FacilityTreeNode;
  /** The selected node's parent name, or "All facilities" for a root — the banner eyebrow. */
  parentLabel: string;
  onOpenManuals: (id: number) => void;
  /** Open this facility's level (its children). */
  onDrill: (id: number) => void;
  /** Navigate into `node`'s level with this child selected. */
  onPickChild: (childId: number) => void;
}) {
  const { t } = useTranslation();
  const hasChildren = node.childCount > 0;
  const canOpenManuals = node.permission !== null && ((node.manualCount ?? 0) > 0 || (node.categoryCount ?? 0) > 0);
  const stats = [
    { key: "sub", Icon: FacilityIcon, value: node.childCount, label: t("facilitySelection.preview.stat.subfacilities", { count: node.childCount }) },
    { key: "cat", Icon: CategoryIcon, value: node.categoryCount ?? 0, label: t("facilitySelection.preview.stat.categories", { count: node.categoryCount ?? 0 }) },
    { key: "man", Icon: ManualIcon, value: node.manualCount ?? 0, label: t("facilitySelection.preview.stat.manuals", { count: node.manualCount ?? 0 }) },
  ];
  // One compact meta line per child ("1 Kategorie · 2 Manuals") from the shared plural keys.
  const metaOf = (c: FacilityTreeNode): string =>
    [
      c.childCount > 0 ? t("counts.subfacilities", { count: c.childCount }) : null,
      (c.categoryCount ?? 0) > 0 ? t("counts.categories", { count: c.categoryCount ?? 0 }) : null,
      (c.manualCount ?? 0) > 0 ? t("counts.manuals", { count: c.manualCount ?? 0 }) : null,
    ]
      .filter((s): s is string => s !== null)
      .join(" · ");

  return (
    // `border-t-0`: the banner IS the panel's visible top edge. A 1px `border-card` line above it is nearly invisible
    // on the muted well, so the panel read 1px lower than the first rail card beside it (owner 2026-10-22).
    <aside
      aria-label={t("facilitySelection.preview.label", { name: node.name })}
      className="border-card bg-background sticky top-6 flex max-h-[calc(100dvh-3rem)] w-[400px] shrink-0 flex-col self-start overflow-hidden rounded-2xl border border-t-0 shadow-[0_1px_2px_rgb(18_36_52/0.05),0_2px_8px_rgb(18_36_52/0.04)]"
    >
      <div className="bg-primary text-primary-foreground flex shrink-0 flex-col gap-3.5 px-[22px] pt-[22px] pb-5">
        <span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-semibold tracking-wider uppercase">
          <Home aria-hidden="true" className="size-3.5 shrink-0" />
          <span className="truncate">{parentLabel}</span>
        </span>
        <h2 className="m-0 text-[26px] leading-8 font-bold tracking-tight break-words">{node.name}</h2>
        <dl className="m-0 grid grid-cols-3 gap-2">
          {stats.map((s) => (
            // A hairline, not a tint: any fill shifts the banner colour and drops the paired primary/primary-foreground
            // contrast (XR · Dark's lightened banner fell below 4.5 with a 15% overlay; Takeda's red is 4.54 to begin with).
            <div key={s.key} className="border-primary-foreground/30 flex flex-col items-center rounded-[10px] border px-1 py-2.5 text-center">
              {/* `hyphens-auto`: Takeda's "Handlungsanweisungen" is wider than the tile. */}
              <dt className="order-2 w-full text-xs leading-4 break-words hyphens-auto">{s.label}</dt>
              <dd className="order-1 m-0 inline-flex items-center gap-1.5 text-[22px] leading-7 font-bold tabular-nums">
                <s.Icon aria-hidden="true" className="size-4 shrink-0" />
                {s.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {canOpenManuals || hasChildren ? (
        <div className="border-card flex shrink-0 gap-2.5 border-b px-[22px] py-[18px]">
          {canOpenManuals ? (
            <NavButton
              variant="accent"
              className="h-11 flex-1 rounded-[10px] text-sm font-semibold"
              aria-label={t("facilitySelection.nav.openManuals", { name: node.name })}
              href={facilityManualsHref(node.id)}
              onNavigate={() => onOpenManuals(node.id)}
            >
              <ManualIcon aria-hidden="true" />
              {t("facilitySelection.nav.manualsButton")}
            </NavButton>
          ) : null}
          {hasChildren ? (
            <NavButton
              variant="outline"
              className="h-11 flex-1 rounded-[10px] text-sm font-semibold"
              aria-label={t("facilitySelection.nav.openSubfacilities", { name: node.name })}
              href={facilityDrillHref(node.id)}
              onNavigate={() => onDrill(node.id)}
            >
              {t("facilitySelection.nav.subfacilitiesButton")}
              <ChevronRight aria-hidden="true" />
            </NavButton>
          ) : null}
        </div>
      ) : null}

      {hasChildren ? (
        <section aria-labelledby={`preview-children-${node.id}`} className="flex min-h-0 flex-col gap-2 overflow-y-auto px-[22px] pt-4 pb-5">
          <div className="flex items-center justify-between gap-2">
            <h3 id={`preview-children-${node.id}`} className="text-muted-foreground m-0 text-xs font-semibold tracking-wider uppercase">
              {t("facilitySelection.preview.subfacilities")}
            </h3>
            {node.children.length > CHILD_PREVIEW ? (
              <AriaButton
                onPress={() => onDrill(node.id)}
                className="text-primary focus-visible:ring-ring/50 cursor-pointer rounded-sm border-0 bg-transparent p-0 text-[13px] font-semibold outline-none hover:underline focus-visible:ring-2"
              >
                {t("facilitySelection.preview.showAll", { count: node.children.length })}
              </AriaButton>
            ) : null}
          </div>
          <ul className="m-0 flex list-none flex-col p-0">
            {node.children.slice(0, CHILD_PREVIEW).map((c) => (
              <li key={c.id} className="border-card border-b last:border-b-0">
                <AriaButton
                  onPress={() => onPickChild(c.id)}
                  className="hover:bg-muted focus-visible:ring-ring/50 text-foreground -mx-2 flex min-h-11 w-[calc(100%+1rem)] cursor-pointer items-center gap-2.5 rounded-lg border-0 bg-transparent px-2 text-left text-sm outline-none focus-visible:ring-2"
                >
                  <span className="min-w-0 flex-1 truncate font-semibold">{c.name}</span>
                  <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{metaOf(c)}</span>
                  <ChevronRight aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
                </AriaButton>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </aside>
  );
}
